import numeral from 'numeral';
import {
    comparator,
    withDefaultsComparator,
    stringComparator
} from '../../utils/table';
import {
    displayPercent,
    DASH_DEFAULT,
    displayShortDate,
    displayWithCommas
    // roundDecimal
} from '../../utils/Format/Format';
import { isMobileWidth } from '../../theme/layoutUtilities';

export const compareDate = (node1, node2, prop) =>
    (node1.data[prop] === DASH_DEFAULT
        ? 0
        : new Date(node1.data[prop]).getTime()) -
    (node2.data[prop] === DASH_DEFAULT
        ? 0
        : new Date(node2.data[prop]).getTime());

const formatCurrency = (num) =>
    numeral(num).format(`$0,0.${new Array(3).join(0)}`);

export const escapeCommas = (val) =>
    typeof val === 'string' ? `"${val}"` : val;

export const getCurrencyFormatter = ({ prop, val, isCsv = false }) => (
    node
) => {
    const finalVal = val === undefined ? node.data[prop] : val;

    if (finalVal === DASH_DEFAULT) {
        return finalVal;
    }

    return isCsv
        ? escapeCommas(formatCurrency(finalVal))
        : formatCurrency(finalVal);
};

export const getNumberFormatter = ({
    prop,
    val,
    isCsv = false,
    showDecimals
}) => (node) => {
    const finalVal = val === undefined ? node.data[prop] : val;

    if (finalVal === DASH_DEFAULT) {
        return finalVal;
    }

    const roundedVal = showDecimals ? finalVal : Math.round(finalVal);
    const formattedVal = displayWithCommas(roundedVal);

    return isCsv ? escapeCommas(formattedVal) : formattedVal;
};

export const getPercentFormatter = ({ prop, val }) => (node) => {
    const finalVal = val === undefined ? node.data[prop] : val;

    return finalVal === DASH_DEFAULT ? finalVal : displayPercent(finalVal, 2);
};

export const getDateFormatter = ({ prop, val }) => (node) => {
    const finalVal = val === undefined ? node.data[prop] : val;

    return finalVal === DASH_DEFAULT ? finalVal : displayShortDate(finalVal);
};

export const getHeaders = (isFixedIncome, isMonthly) => [
    {
        headerName: 'Name',
        field: 'name',
        autoHeight: true,
        minWidth: isMobileWidth() ? 100 : 200,
        pinned: 'left',
        cellClass: ['row-value', 'ag-cell-value-text'],
        comparator: stringComparator
    },
    {
        headerName: '% of Fund',
        headerClass: ['ag-header-cell-number'],
        field: 'percentTotalNetAssets',
        sort: 'desc',
        cellClass: ['cell-wrap-text', 'row-value', 'ag-cell-value-number'],
        minWidth: 80,
        valueFormatter: getPercentFormatter({
            prop: 'percentTotalNetAssets'
        }),
        comparator: withDefaultsComparator
    },
    {
        headerName: 'Ticker',
        field: 'ticker',
        minWidth: 60,
        autoHeight: true,
        cellClass: ['row-value', 'ag-cell-value-text'],
        comparator: stringComparator,
        hide: isMonthly,
        suppressToolPanel: isMonthly
    },
    {
        headerName: 'Identifier',
        field: 'prioritizedIdentifier',
        minWidth: 90,
        autoHeight: true,
        cellClass: ['row-value', 'ag-cell-value-text'],
        comparator: stringComparator,
        hide: isMonthly,
        suppressToolPanel: isMonthly
    },
    {
        headerName: 'Investment Type',
        field: 'investmentType',
        minWidth: 110,
        autoHeight: true,
        cellClass: ['row-value', 'ag-cell-value-text'],
        comparator: stringComparator,
        hide: isMonthly,
        suppressToolPanel: isMonthly
    },
    {
        headerName: '&nbsp; &nbsp; Options Strike&nbsp;Price',
        headerClass: ['ag-header-cell-number'],
        field: 'strikePrice',
        cellClass: ['cell-wrap-text', 'row-value', 'ag-cell-value-number'],
        minWidth: 90,
        valueFormatter: getCurrencyFormatter({ prop: 'strikePrice' }),
        comparator,
        hide: isMonthly,
        suppressToolPanel: isMonthly
    },
    {
        headerName: 'Coupon %',
        headerClass: ['ag-header-cell-number'],
        field: 'holdingCouponRate',
        cellClass: ['cell-wrap-text', 'row-value', 'ag-cell-value-number'],
        minWidth: 80,
        valueFormatter: getPercentFormatter({ prop: 'holdingCouponRate' }),
        comparator: withDefaultsComparator,
        hide: !(isFixedIncome || isMonthly),
        suppressToolPanel: !(isFixedIncome || isMonthly)
    },
    {
        headerName: 'Maturity',
        headerClass: ['ag-header-cell-number'],
        field: 'holdingMaturityDate',
        minWidth: 80,
        autoHeight: true,
        cellClass: ['row-value', 'ag-cell-value-number'],
        valueFormatter: getDateFormatter({ prop: 'holdingMaturityDate' }),
        comparator: (a, b, node1, node2) =>
            compareDate(node1, node2, 'holdingMaturityDate'),
        hide: !(isFixedIncome || isMonthly),
        suppressToolPanel: !(isFixedIncome || isMonthly)
    },
    {
        headerName: '&nbsp; &nbsp; Security / Asset&nbsp;Quantity',
        headerClass: ['ag-header-cell-number'],
        field: 'shareQuantity',
        cellClass: ['cell-wrap-text', 'row-value', 'ag-cell-value-number'],
        minWidth: 110,
        valueFormatter: getNumberFormatter({
            prop: 'shareQuantity',
            showDecimals: !isMonthly
        }),
        comparator
    },
    {
        headerName: 'Issue Date',
        field: 'issueDate',
        minWidth: 90,
        autoHeight: true,
        cellClass: ['row-value', 'ag-cell-value-text'],
        valueFormatter: getDateFormatter({ prop: 'issueDate' }),
        comparator: (_a, _b, node1, node2) =>
            compareDate(node1, node2, 'issueDate'),
        hide: !isFixedIncome || isMonthly,
        suppressToolPanel: !isFixedIncome || isMonthly
    },
    {
        headerName: 'Market Value',
        headerClass: ['ag-header-cell-number'],
        field: 'marketValue',
        cellClass: ['cell-wrap-text', 'row-value', 'ag-cell-value-number'],
        minWidth: 120,
        valueFormatter: getCurrencyFormatter({ prop: 'marketValue' }),
        comparator
    }
];
